import type { TFunction } from 'i18next';
import type { MovementReportSuiteTabId } from '../model/movement-report-suite-prototype';

export const ANALYTICS_MOVE_TAB_OVERVIEW = 'overview';
export const ANALYTICS_MOVE_TAB_REVENUE = 'revenue';
export const ANALYTICS_MOVE_TAB_MEMBERS = 'members';
export const ANALYTICS_MOVE_TAB_REGISTRATIONS = 'registrations';
export const ANALYTICS_MOVE_TAB_EDUCATION = 'education';
export const ANALYTICS_MOVE_TAB_COURSES = 'courses';
export const ANALYTICS_MOVE_TAB_CERTIFICATIONS = 'certifications';
export const ANALYTICS_MOVE_TAB_REGISTRATION_TOP30 = 'registration_top30';
export const ANALYTICS_MOVE_TAB_MEMBERSHIP = 'membership';
export const ANALYTICS_MOVE_TAB_MAPS = 'maps';
export const ANALYTICS_MOVE_TAB_MAIN_LOCATION = 'main_location';
export const ANALYTICS_MOVE_TAB_TRANSLATIONS = 'translations';
export const ANALYTICS_MOVE_TAB_TRAINING_PROGRAMS = 'training_programs';
export const ANALYTICS_MOVE_TAB_WEEKLY_REPORT = 'weekly-report';

export type AnalyticsMoveTopLevelTabId =
  | typeof ANALYTICS_MOVE_TAB_OVERVIEW
  | typeof ANALYTICS_MOVE_TAB_REVENUE
  | typeof ANALYTICS_MOVE_TAB_MEMBERS
  | typeof ANALYTICS_MOVE_TAB_REGISTRATIONS
  | typeof ANALYTICS_MOVE_TAB_EDUCATION
  | typeof ANALYTICS_MOVE_TAB_COURSES
  | typeof ANALYTICS_MOVE_TAB_CERTIFICATIONS
  | typeof ANALYTICS_MOVE_TAB_REGISTRATION_TOP30
  | typeof ANALYTICS_MOVE_TAB_MEMBERSHIP
  | typeof ANALYTICS_MOVE_TAB_MAPS
  | typeof ANALYTICS_MOVE_TAB_MAIN_LOCATION
  | typeof ANALYTICS_MOVE_TAB_TRANSLATIONS
  | typeof ANALYTICS_MOVE_TAB_TRAINING_PROGRAMS
  | typeof ANALYTICS_MOVE_TAB_WEEKLY_REPORT;

export type AnalyticsMoveTabPath =
  | '/$locale/analytics-move/overview'
  | '/$locale/analytics-move/weekly'
  | '/$locale/analytics-move/revenue'
  | '/$locale/analytics-move/members'
  | '/$locale/analytics-move/registrations'
  | '/$locale/analytics-move/education'
  | '/$locale/analytics-move/courses'
  | '/$locale/analytics-move/certifications'
  | '/$locale/analytics-move/registration-top30'
  | '/$locale/analytics-move/membership'
  | '/$locale/analytics-move/maps'
  | '/$locale/analytics-move/main-location'
  | '/$locale/analytics-move/translations'
  | '/$locale/analytics-move/training-programs';

type AnalyticsMoveTabPanel =
  | 'overview'
  | 'weekly'
  | 'report-suite'
  | 'maps'
  | 'main-location'
  | 'translations';

type AnalyticsMoveTabConfig = {
  id: AnalyticsMoveTopLevelTabId;
  path: AnalyticsMoveTabPath;
  labelKey: string;
  fallbackLabel: string;
  panel: AnalyticsMoveTabPanel;
};

const ANALYTICS_MOVE_TAB_CONFIG: readonly AnalyticsMoveTabConfig[] = [
  {
    id: ANALYTICS_MOVE_TAB_OVERVIEW,
    path: '/$locale/analytics-move/overview',
    labelKey: 'analytics.move.tabs.overview',
    fallbackLabel: 'Overview',
    panel: 'overview',
  },
  {
    id: ANALYTICS_MOVE_TAB_WEEKLY_REPORT,
    path: '/$locale/analytics-move/weekly',
    labelKey: 'analytics.move.tabs.weeklyReport',
    fallbackLabel: 'Weekly',
    panel: 'weekly',
  },
  {
    id: ANALYTICS_MOVE_TAB_REVENUE,
    path: '/$locale/analytics-move/revenue',
    labelKey: 'analytics.reportSuite.tabs.revenue',
    fallbackLabel: 'Revenue',
    panel: 'report-suite',
  },
  {
    id: ANALYTICS_MOVE_TAB_MEMBERS,
    path: '/$locale/analytics-move/members',
    labelKey: 'analytics.reportSuite.tabs.members',
    fallbackLabel: 'Members',
    panel: 'report-suite',
  },
  {
    id: ANALYTICS_MOVE_TAB_REGISTRATIONS,
    path: '/$locale/analytics-move/registrations',
    labelKey: 'analytics.reportSuite.tabs.registrations',
    fallbackLabel: 'Registrations',
    panel: 'report-suite',
  },
  {
    id: ANALYTICS_MOVE_TAB_EDUCATION,
    path: '/$locale/analytics-move/education',
    labelKey: 'analytics.reportSuite.tabs.education',
    fallbackLabel: 'Education',
    panel: 'report-suite',
  },
  {
    id: ANALYTICS_MOVE_TAB_COURSES,
    path: '/$locale/analytics-move/courses',
    labelKey: 'analytics.reportSuite.tabs.courses',
    fallbackLabel: 'Courses',
    panel: 'report-suite',
  },
  {
    id: ANALYTICS_MOVE_TAB_CERTIFICATIONS,
    path: '/$locale/analytics-move/certifications',
    labelKey: 'analytics.reportSuite.tabs.certifications',
    fallbackLabel: 'Certifications',
    panel: 'report-suite',
  },
  {
    id: ANALYTICS_MOVE_TAB_REGISTRATION_TOP30,
    path: '/$locale/analytics-move/registration-top30',
    labelKey: 'analytics.reportSuite.tabs.registrationTop30',
    fallbackLabel: 'Top 30',
    panel: 'report-suite',
  },
  {
    id: ANALYTICS_MOVE_TAB_MEMBERSHIP,
    path: '/$locale/analytics-move/membership',
    labelKey: 'analytics.reportSuite.tabs.membership',
    fallbackLabel: 'Membership',
    panel: 'report-suite',
  },
  {
    id: ANALYTICS_MOVE_TAB_TRAINING_PROGRAMS,
    path: '/$locale/analytics-move/training-programs',
    labelKey: 'analytics.reportSuite.tabs.trainingPrograms',
    fallbackLabel: 'Training',
    panel: 'report-suite',
  },
  {
    id: ANALYTICS_MOVE_TAB_MAPS,
    path: '/$locale/analytics-move/maps',
    labelKey: 'analytics.move.tabs.maps',
    fallbackLabel: 'Maps',
    panel: 'maps',
  },
  {
    id: ANALYTICS_MOVE_TAB_MAIN_LOCATION,
    path: '/$locale/analytics-move/main-location',
    labelKey: 'analytics.move.tabs.mainLocation',
    fallbackLabel: 'Main Location',
    panel: 'main-location',
  },
  {
    id: ANALYTICS_MOVE_TAB_TRANSLATIONS,
    path: '/$locale/analytics-move/translations',
    labelKey: 'analytics.move.tabs.translations',
    fallbackLabel: 'Translations',
    panel: 'translations',
  },
];

const ANALYTICS_MOVE_TAB_CONFIG_BY_ID = Object.fromEntries(
  ANALYTICS_MOVE_TAB_CONFIG.map((item) => [item.id, item])
) as Record<AnalyticsMoveTopLevelTabId, AnalyticsMoveTabConfig>;

const LEGACY_ANALYTICS_MOVE_TAB_ALIASES: Partial<Record<string, AnalyticsMoveTopLevelTabId>> = {
  registration_analytics: ANALYTICS_MOVE_TAB_REGISTRATIONS,
  em_analytics: ANALYTICS_MOVE_TAB_EDUCATION,
  course_breakdown: ANALYTICS_MOVE_TAB_COURSES,
  membership_analytics: ANALYTICS_MOVE_TAB_MEMBERSHIP,
  course_calendar: ANALYTICS_MOVE_TAB_COURSES,
  certifications_breakdown: ANALYTICS_MOVE_TAB_CERTIFICATIONS,
  top30_instructors: ANALYTICS_MOVE_TAB_REGISTRATION_TOP30,
  instructo_performance: ANALYTICS_MOVE_TAB_MEMBERSHIP,
  financial_dashboard: ANALYTICS_MOVE_TAB_REVENUE,
  instructor_analytics: ANALYTICS_MOVE_TAB_MAPS,
  freediver_analytics: ANALYTICS_MOVE_TAB_MAPS,
  schools_analytics: ANALYTICS_MOVE_TAB_MAPS,
  main_locations: ANALYTICS_MOVE_TAB_MAIN_LOCATION,
  translations: ANALYTICS_MOVE_TAB_TRANSLATIONS,
};

export const ANALYTICS_MOVE_TOP_LEVEL_TABS = ANALYTICS_MOVE_TAB_CONFIG.map((item) => item.id);
export const ANALYTICS_MOVE_REPORT_SUITE_TABS = ANALYTICS_MOVE_TAB_CONFIG.filter(
  (item) => item.panel === 'report-suite'
).map((item) => item.id) as MovementReportSuiteTabId[];

export function getAnalyticsMoveTabItems(t: TFunction) {
  return ANALYTICS_MOVE_TAB_CONFIG.map((item) => ({
    id: item.id,
    label: t(item.labelKey, item.fallbackLabel),
  }));
}

export function getAnalyticsMoveTabPath(tabId: AnalyticsMoveTopLevelTabId): AnalyticsMoveTabPath {
  return ANALYTICS_MOVE_TAB_CONFIG_BY_ID[tabId].path;
}

export function getAnalyticsMoveTabPanel(tabId: AnalyticsMoveTopLevelTabId): AnalyticsMoveTabPanel {
  return ANALYTICS_MOVE_TAB_CONFIG_BY_ID[tabId].panel;
}

export function isAnalyticsMoveTopLevelTabId(value: unknown): value is AnalyticsMoveTopLevelTabId {
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(ANALYTICS_MOVE_TAB_CONFIG_BY_ID, value)
  );
}

export function isMovementReportSuiteTopLevelTabId(
  value: AnalyticsMoveTopLevelTabId
): value is MovementReportSuiteTabId {
  return ANALYTICS_MOVE_REPORT_SUITE_TABS.includes(value as MovementReportSuiteTabId);
}

export function resolveAnalyticsMoveTabId(
  value: unknown,
  fallback: AnalyticsMoveTopLevelTabId = ANALYTICS_MOVE_TAB_OVERVIEW
): AnalyticsMoveTopLevelTabId {
  if (isAnalyticsMoveTopLevelTabId(value)) {
    return value;
  }

  if (typeof value === 'string') {
    return LEGACY_ANALYTICS_MOVE_TAB_ALIASES[value] ?? fallback;
  }

  return fallback;
}

export function resolveAnalyticsMoveTabPath(value: unknown): AnalyticsMoveTabPath {
  return getAnalyticsMoveTabPath(resolveAnalyticsMoveTabId(value));
}
